import { saveReservation, type ReservationData } from '../../services/reservationService';
import type { CartItem } from '../../services/cartService';
import type { User as UserType } from '../../types';
import type { CheckoutShoppingCartSlice, CheckoutTotals } from './checkoutFormTotals';

export type CheckoutFormFieldState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  hearAboutUs?: string;
  paymentMethod: string;
  couponCode: string;
};

export type CheckoutPersistResult = {
  savedReservations: ReservationData[];
  submittedReservationId: string | null;
  submittedReservationData: {
    orderNumber: string | undefined;
    customerName: string;
    totalAmount: string;
    items: string[];
  };
  parentSubmitPayload: {
    reservations: ReservationData[];
    paymentMode: 'guest' | 'member';
    totals: CheckoutTotals;
  };
};

export function persistCheckoutReservations(params: {
  selectedItems: CartItem[];
  shoppingCartData?: CheckoutShoppingCartSlice;
  formData: CheckoutFormFieldState;
  paymentMode: 'guest' | 'member';
  isLoggedIn: boolean;
  loggedInUserId: string | null;
  currentUser: UserType | null;
  totals: CheckoutTotals;
  cloverChargeId?: string;
}): CheckoutPersistResult {
  const {
    selectedItems,
    shoppingCartData,
    formData,
    paymentMode,
    isLoggedIn,
    loggedInUserId,
    currentUser,
    totals,
    cloverChargeId,
  } = params;

  const isGuest = paymentMode === 'guest' || !isLoggedIn;
  const groups = shoppingCartData?.groups || undefined;
  const checkIn = shoppingCartData?.startDate
    ? shoppingCartData.startDate.toISOString().split('T')[0]
    : new Date().toISOString();
  const checkOut = shoppingCartData?.endDate
    ? shoppingCartData.endDate.toISOString().split('T')[0]
    : checkIn;
  const checkoutId = `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const paid = !!cloverChargeId;
  const finalPaymentStatus: 'pending' | 'paid' = paid ? 'paid' : 'pending';
  const finalStatus: 'pending' | 'confirmed' = cloverChargeId ? 'confirmed' : 'pending';
  const paymentMethod = 'full';

  const savedReservations = selectedItems.map((item) => {
    let itemTotalPrice = item.itemPrice;
    if (item.type === 'accommodation') itemTotalPrice = item.itemPrice * totals.duration;
    else if (item.type === 'activity') itemTotalPrice = item.itemPrice * totals.totalGuests;

    const customerName = isGuest
      ? `${formData.firstName} ${formData.lastName}`
      : currentUser
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : '';
    const customerEmail = isGuest ? formData.email : currentUser ? currentUser.email : '';
    const customerPhone = isGuest ? formData.phone : currentUser ? currentUser.phone || '' : '';

    const reservationToSave: Omit<ReservationData, 'id' | 'createdAt'> = {
      userId: isGuest ? undefined : loggedInUserId || undefined,
      guestName: isGuest ? customerName : undefined,
      guestEmail: customerEmail,
      guestPhone: customerPhone,
      itemType: item.type,
      itemId: item.itemId,
      itemName: item.itemName,
      checkIn,
      checkOut,
      guests: totals.totalGuests,
      totalPrice: itemTotalPrice,
      status: finalStatus,
      paymentStatus: finalPaymentStatus,
      paymentMethod: cloverChargeId
        ? `${paymentMethod}:clover:${cloverChargeId}`
        : paymentMethod,
      depositAmount: undefined,
      balanceAmount: undefined,
      gstAmount: totals.gst,
      qstAmount: totals.qst,
      taxeHebergementAmount: totals.taxeHebergement > 0 ? totals.taxeHebergement : undefined,
      specialRequests: formData.specialRequests,
      hearAboutUs: formData.hearAboutUs || undefined,
      isGuest,
      duration: totals.duration,
      groups,
      checkoutId,
    };
    return saveReservation(reservationToSave);
  });

  const first = savedReservations[0];
  const submittedReservationData = {
    orderNumber: first?.id,
    customerName: isGuest
      ? `${formData.firstName} ${formData.lastName}`
      : currentUser
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : `${formData.firstName} ${formData.lastName}`,
    totalAmount: `$${totals.total.toFixed(2)} CAD`,
    items: selectedItems.map((item) => item.itemName),
  };

  return {
    savedReservations,
    submittedReservationId: first?.id ?? null,
    submittedReservationData,
    parentSubmitPayload: {
      reservations: savedReservations,
      paymentMode,
      totals,
    },
  };
}
